import { memo, useMemo, useState } from 'react';

import { CurrencyCodeType } from '@mint-lib/currencies-helpers';
import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import styled, { css, isPropValid } from '@mint-lib/styled';

import { Icon } from '@myn/mui';

import InsertionOrderModal from '../../../atoms/InsertionOrderModal/InsertionOrderModal.jsx';
import { useGetMediaPlanRows } from '../../../hooks/useGetMediaPlanRows.js';
import useIsBuyingPlanLocked from '../../../hooks/useIsBuyingPlanLocked.js';
import { useMediaPlanRowUpdate } from '../../../hooks/useMediaPlanRowUpdate.js';
import { usePredictivePlanningMode } from '../../../hooks/usePredictivePlanningMode.js';
import DeleteRowModal from '../../../molecules/DeleteRowModal/DeleteRowModal.jsx';
import TableSkeleton from '../../../organisms/PageSkeleton/TableSkeleton.jsx';
import { DragLayerRow } from '../AddRowsPanel/DragLayerRow.jsx';
import AddRowsPanel from '../AddRowsPanel/index.js';
import { TabStateType } from '../BuilderHeader/BuilderHeader.jsx';
import Panel from '../Panel.jsx';
import { PanelTab } from '../PanelTab.js';
import { RowToChangeCurrencyObject, RowToRemoveObject } from '../types.js';
import BuilderTable from './BuilderTable/BuilderTable.jsx';
import ChangeRowCurrencyModal from './ChangeRowCurrencyModal/ChangeRowCurrencyModal.jsx';
import DropContainer from './DropContainer.jsx';
import NoMediaRows from './NoMediaRows.jsx';
import RowReorderDragItem from './RowReorderDragItem.jsx';
import useBuilderTable from './useBuilderTable.jsx';

const BUILDER_HEADER_CONTENT = 275;
const PREDICTIVE_SETTINGS_CONTENT = 313;

type Props = {
  tabState: TabStateType;
  isLoading: boolean;
};

const BuilderBody = memo(function BuilderBody({ tabState, isLoading }: Props) {
  const { t } = useTranslation('@myn/planning');

  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const { isLocked: isBuyingPlanLocked } = useIsBuyingPlanLocked();

  const { updateRow, isLoading: isRowUpdating } = useMediaPlanRowUpdate();

  const isPredictive = usePredictivePlanningMode();

  const preparedRows = useGetMediaPlanRows();

  const [rowToRemove, setRowToRemove] = useState<RowToRemoveObject | null>(
    null,
  );

  const [rowToChangeCurrency, setRowToChangeCurrency] =
    useState<RowToChangeCurrencyObject | null>(null);

  const tableProps = useBuilderTable({
    setRowToRemove,
    setRowToChangeCurrency,
  });

  const handleCloseChangeRowCurrencyModal = () => setRowToChangeCurrency(null);

  const handleChangeRowCurrency = async (currency: CurrencyCodeType | null) => {
    if (!rowToChangeCurrency) {
      return;
    }

    await updateRow(rowToChangeCurrency.rowId, currency);

    handleCloseChangeRowCurrencyModal();
  };

  const heightToDeduct = useMemo(
    () => (isPredictive ? PREDICTIVE_SETTINGS_CONTENT : BUILDER_HEADER_CONTENT),
    [isPredictive],
  );

  const tableContent = preparedRows?.length ? (
    <BuilderTable {...tableProps} />
  ) : (
    <NoMediaRows setAddRowsPanel={tabState.setCurrentTab} />
  );

  return (
    <Wrapper
      id="builderTable"
      $isEmpty={!preparedRows.length && !isLoading}
      heightToDeduct={heightToDeduct}
    >
      {isLoading ? <TableSkeleton /> : tableContent}

      <DeleteRowModal
        rowToRemove={rowToRemove}
        setRowToRemove={setRowToRemove}
      />

      <ChangeRowCurrencyModal
        open={!!rowToChangeCurrency}
        onClose={handleCloseChangeRowCurrencyModal}
        onSubmit={handleChangeRowCurrency}
        rowData={rowToChangeCurrency}
        isRowCurrencySending={isRowUpdating}
      />
      <InsertionOrderModal />
      <Panel
        otherTab={tabState.currentTab}
        setOtherTab={tabState.setCurrentTab}
        tabsBefore={[
          {
            icon: (
              <Icon
                component="RowInsert"
                fontSize="large"
                aria-label="add channel"
              />
            ),
            title: t('Add new channel'),
            value: PanelTab.AddRow,
            isHidden: !isAllowed || isBuyingPlanLocked,
            tooltip: t('Add channel'),
            content: (
              <AddRowsPanel
                toggleRowExpanded={tableProps.toggleRowExpanded}
                setRowState={tableProps.setRowState}
              />
            ),
          },
        ]}
      />
      <DragLayerRow />
      <RowReorderDragItem />
      <DropContainer
        rows={preparedRows}
        toggleRowExpanded={tableProps.toggleRowExpanded}
        setRowState={tableProps.setRowState}
      />
    </Wrapper>
  );
});

const Wrapper = styled('div', { shouldForwardProp: isPropValid })<{
  heightToDeduct: number;
  $isEmpty?: boolean;
}>`
  position: relative;
  height: 100%;

  ${({ $isEmpty, theme }) =>
    $isEmpty &&
    css`::before {
            position: absolute;
            content: '';
            width: 100%;
            height: 100%;
            border: 1px solid ${theme.palette.silver.main};`}
  border-radius: 4px;
`;

export default BuilderBody;
